import React, { useState } from "react";
import { Box, Paper, Typography, TextField, Button } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import API from "../utils/api";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import { validatePassword } from "../utils/validatePassword";
import PasswordRequirements from "../components/PasswordRequirements";

export default function ResetPassword() {
  const { uid, token } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();

  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Run password validation first
    const errors = validatePassword(form.password);
    if (errors.length > 0) {
      setError(errors.join("\n"));
      return;
    }

    // Clear old error messages
    setError("");

    // Confirm match
    if (form.password !== form.confirm) {
      return toast.error("❌ Passwords do not match.");
    }

    setLoading(true);

    try {
      await API.post(`/auth/reset/${uid}/${token}/`, {
        new_password1: form.password,
        new_password2: form.confirm,
      });

      toast.success("Password reset successful!");
      navigate("/login");
    } catch (err) {
      console.error("Reset error:", err);
      toast.error("❌ Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
        px: 2,
      }}
    >
      <Paper
        elevation={4}
        sx={{
          p: { xs: 3, sm: 5 },
          width: "100%",
          maxWidth: 420,
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" color="primary" fontWeight={700} mb={1}>
          Reset Password
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Enter a new password below.
        </Typography>

        {/* Show error messages */}
        {error && (
          <Typography
            variant="body2"
            color="error"
            sx={{ whiteSpace: "pre-line", mb: 2 }}
          >
            {error}
          </Typography>
        )}

        <PasswordRequirements />

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type="password"
            label="New Password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm Password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? "Saving..." : "Reset Password"}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
